import { useId, useLayoutEffect, useRef, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from "react";
import { motion } from "motion/react";
import { useT } from "../i18n/index.js";
import { useReducedMotion } from "../units/useReducedMotion.js";
import { Icon } from "./Icon.js";
import "./dialog.css";

export interface DialogProps {
  title: ReactNode;
  children: ReactNode;
  onClose: () => void;
  actions?: ReactNode;
  footer?: ReactNode;
  testId?: string | undefined;
  wide?: boolean;
  surface?: "modal" | "sheet";
  modal?: boolean;
}

/** Native focus containment for modals; base inspection sheets remain nonmodal. */
export function Dialog({ title, children, onClose, actions, footer, testId, wide = false, surface = "modal", modal = true }: DialogProps) {
  const t = useT();
  const reduced = useReducedMotion();
  const ref = useRef<HTMLDialogElement>(null);
  const labelId = useId();
  useLayoutEffect(() => {
    const dialog = ref.current!;
    const previous = document.activeElement;
    if (modal) dialog.showModal(); else dialog.show();
    return () => {
      const active = document.activeElement;
      const restorePrevious = !active || active === document.body || !active.isConnected || dialog.contains(active);
      dialog.close();
      const destination = restorePrevious ? previous : active;
      if (destination instanceof HTMLElement && destination.isConnected) destination.focus({ preventScroll: true });
    };
  }, [modal]);
  useLayoutEffect(() => {
    if (modal) return;
    const dismiss = (event: KeyboardEvent) => {
      const top = [...document.querySelectorAll("dialog[open]")].at(-1);
      if (event.key === "Escape" && !event.defaultPrevented && top === ref.current && !document.querySelector("dialog:modal")) { event.preventDefault(); onClose(); }
    };
    document.addEventListener("keydown", dismiss);
    return () => document.removeEventListener("keydown", dismiss);
  }, [modal, onClose]);
  const containTab = (event: ReactKeyboardEvent<HTMLDialogElement>) => {
    const dialog = event.currentTarget;
    if (!modal || event.key !== "Tab" || event.defaultPrevented || !(event.target instanceof Element) || event.target.closest("dialog") !== dialog) return;
    // Native modal inertness blocks the page, but Tab can still leave for browser chrome.
    const stops = [...dialog.querySelectorAll<HTMLElement>("a[href], button, input, select, textarea, [tabindex], [contenteditable=true]")]
      .filter(element => element.tabIndex >= 0 && !element.matches(":disabled") && !element.closest("[inert]") && element.closest("dialog") === dialog && element.getClientRects().length > 0 && getComputedStyle(element).visibility === "visible")
      .sort((a, b) => (a.tabIndex || Infinity) - (b.tabIndex || Infinity));
    const first = stops[0], last = stops.at(-1);
    if (!first) { event.preventDefault(); dialog.focus(); }
    else if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog)) { event.preventDefault(); last!.focus(); }
    else if (!event.shiftKey && (document.activeElement === last || document.activeElement === dialog)) { event.preventDefault(); first.focus(); }
  };
  return (
    <dialog ref={ref} tabIndex={-1} className={`glass ui-dialog ${surface === "sheet" ? "sheet" : "modal"} ${reduced ? "reduced" : ""}`} data-testid={testId} data-wide={wide} aria-labelledby={labelId} aria-modal={modal}
      onKeyDown={containTab}
      onCancel={event => { event.preventDefault(); onClose(); }}
      onClick={event => { if (!modal || event.target !== event.currentTarget) return; const box = event.currentTarget.getBoundingClientRect(); if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) onClose(); }}>
      <motion.div className="dialog-content" initial={reduced ? false : { opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={reduced ? { duration: 0 } : { type: "spring", stiffness: 310, damping: 30 }}>
        <header className="row sheet-head">
          <h2 id={labelId} className="grow">{title}</h2>
          {actions}
          <button type="button" className="sheet-close" onClick={onClose} aria-label={t("common.close")} data-testid="sheet-close"><Icon name="close" /></button>
        </header>
        <div className="sheet-body">{children}</div>
        {footer ? <footer className="dialog-footer">{footer}</footer> : null}
      </motion.div>
    </dialog>
  );
}
